import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { Helmet } from 'react-helmet';
import { useStores } from '../../hooks/use-stores';
import BlockWithRightAlignedImage from './block-with-right-aligned-image';
import ContentFilters from './content-filters';
import ContentResultSection from './content-result-section';
import SubjectPreview from '../subjectPreview';
import PreviewQuestion from '../../components/manager/assessment/preview-question';
import Modal from 'react-modal';
import MaterialModal from './material-modal';
import { useTranslation } from 'react-i18next';
import { ThemedButton, FlexDiv } from '@awarego-ui/components';
import ScormContentModal from './ScormContentModal';

function Content({
    companyId = null,
    partnerId = null,
    partnerOnly = false,
    defaultLanguage = 'en',
}) {
    const { brandingStore, resourcesStore, store, catalogStore, cmi5Store } =
        useStores();
    const { questionsEditorVisible, trainingsVisible, assessmentsVisible, materialsVisible } = brandingStore;
    const { currentCompany } = store;
    const effectiveCompanyId = partnerOnly
        ? null
        : companyId || currentCompany?.company_id;
    const effectiveDefaultLanguage =
        defaultLanguage || currentCompany?.defaultLanguage || 'en';
    const {
        loadingContent,
        trainingContent,
        assessmentContent,
        materialContent,
    } = resourcesStore;
    const { i18n } = useTranslation('assessment');
    const navigate = useNavigate();
    /** How many items are shown initially, before 'Show all' is selected */
    const initialItemCount = 8;

    const [subjectModalIsOpen, setSubjectModalIsOpen] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [showingAllSubjects, showAllSubjects] = useState(false);

    const [questionModalIsOpen, setQuestionModalIsOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [showingAllQuestions, showAllQuestions] = useState(false);

    const [materialModalIsOpen, setMaterialModalIsOpen] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [showingAllMaterial, showAllMaterial] = useState(false);

    const [isAddContentModalOpen, setAddContentModalOpen] = useState(false);

    const redirectToUpgrade = () => {
        navigate('subscriptions/upgrade');
    };

    useEffect(() => {
        doLoadData();
    }, [effectiveCompanyId, partnerId]);

    const doLoadData = async () => {
        if (!effectiveCompanyId && !partnerId) return;
        await resourcesStore.loadAllContent(effectiveCompanyId, partnerId);
        await catalogStore.loadCatalog(effectiveCompanyId, partnerId);
        await catalogStore.loadThreatAreaCatalog(effectiveCompanyId, partnerId);
    };

    const doShowAllSubjects = () => {
        showAllSubjects(true);
    };
    const doShowAllQuestions = () => {
        showAllQuestions(true);
    };
    const doShowAllMaterial = () => {
        showAllMaterial(true);
    };

    /** Get the items that should be displayed, either all or only the first X*/
    const getDisplayItems = (showingFlag, items) => {
        return showingFlag ? items : items.slice(0, initialItemCount);
    };

    /** When user closes Question preview (after maybe viewing the question in non-english, we reset the system language to company's default language */
    const resetSelectedLanguage = () => {
        i18n.changeLanguage(effectiveDefaultLanguage);
    };

    const openSubjectModal = (subjectId, subject) => {
        // if subject is uploaded scorm, Custom Content means only scorm?
        if (!partnerId && subject.tags.includes('Custom Content')) {
            navigate(`/content/${subjectId}`);
        } else {
            setSubjectModalIsOpen(true);
            setSelectedSubject({ id: subjectId, item: subject });
        }
    };

    const closeSubjectModal = () => {
        setSubjectModalIsOpen(false);
    };

    const openQuestionModal = (questionId) => {
        setQuestionModalIsOpen(true);
        setSelectedQuestion(questionId);
    };

    const closeQuestionModal = () => {
        setQuestionModalIsOpen(false);
        resetSelectedLanguage();
    };

    const openMaterialModal = (materialId, material) => {
        setMaterialModalIsOpen(true);
        setSelectedMaterial(material);
    };

    const closeMaterialModal = () => {
        setMaterialModalIsOpen(false);
    };

    const createAddContentButton = () => (
        <ThemedButton
            text={'Import Scorm'}
            mode={'secondary'}
            onClick={() => {
                setAddContentModalOpen(true);
            }}
            $noWrap
            $noTextTransform
        />
    );

    const createQuestionButton = () => (
        <ThemedButton
            text={'Create Question'}
            mode={'primary'}
            onClick={() => {
                navigate('/content/question-editor');
            }}
            $noWrap
            $noTextTransform
        />
    );

    return (
        <Container className="manager-content">
            <Helmet>
                <title>Content</title>
            </Helmet>
            <BlockWithRightAlignedImage
                heading={
                    (() => {
                        const parts = [
                            trainingsVisible && 'subjects',
                            assessmentsVisible && 'scenarios',
                            materialsVisible && 'materials',
                        ].filter(Boolean);
                        if (parts.length === 0) return 'Content';
                        const joined = parts.join(', ').replace(/, ([^,]*)$/, ' and $1');
                        return joined.charAt(0).toUpperCase() + joined.slice(1);
                    })()
                }
                text={
                    [
                        (trainingsVisible || assessmentsVisible) &&
                            `Here you can browse all current ${[
                                trainingsVisible && 'training subjects',
                                assessmentsVisible && 'assessment scenarios',
                            ]
                                .filter(Boolean)
                                .join(' and ')} in all available languages.`,
                        materialsVisible &&
                            `You can also find shareable and printable materials to support your security awareness programs. These include posters, stickers for social media, e-books and whitepapers to help reinforce good cybersecurity habits at your workplace.`,
                    ]
                        .filter(Boolean)
                        .join(' ')
                }
                img={'/static/img/resources-top-img-LowRes-2.jpg'}
                imgAlt={'our content'}
            />

            <ContentFilters />

            {loadingContent ? (
                <ThemedSpinner />
            ) : (
                <>
                    {trainingsVisible && (
                        <ContentResultSection
                            heading={'Training subjects'}
                            items={getDisplayItems(
                                showingAllSubjects,
                                trainingContent
                            )}
                            totalItemCount={trainingContent.length}
                            fullWidthThumb
                            handleClick={openSubjectModal}
                            handleShowAll={doShowAllSubjects}
                            buttons={
                                store.isAdmin && !partnerId
                                    ? createAddContentButton()
                                    : null
                            }
                        />
                    )}
                    {assessmentsVisible && (
                        <ContentResultSection
                            heading={'Risk assessment scenarios'}
                            items={getDisplayItems(
                                showingAllQuestions,
                                assessmentContent
                            )}
                            totalItemCount={assessmentContent.length}
                            fullWidthThumb
                            handleClick={openQuestionModal}
                            handleShowAll={doShowAllQuestions}
                            buttons={
                                store.isAdmin && questionsEditorVisible && !partnerId ? (
                                    <FlexDiv gap={8}>
                                        {createQuestionButton()}
                                    </FlexDiv>
                                ) : null
                            }
                        />
                    )}
                    {materialsVisible && (
                        <ContentResultSection
                            heading={'Reinforcement material'}
                            items={getDisplayItems(
                                showingAllMaterial,
                                materialContent
                            )}
                            totalItemCount={materialContent.length}
                            fullWidthThumb
                            handleClick={openMaterialModal}
                            handleShowAll={doShowAllMaterial}
                        />
                    )}
                </>
            )}

            {/* Subject modal*/}
            {selectedSubject && (
                <SubjectPreview
                    subjectId={selectedSubject.id}
                    isOpen={subjectModalIsOpen}
                    onClose={closeSubjectModal}
                    hideFooter={true}
                    showLanguageSelector={true}
                    availableLanguages={selectedSubject.item.languages || []}
                    defaultLanguage={effectiveDefaultLanguage}
                    partnerId={partnerId}
                    companyId={effectiveCompanyId}
                    redirectToUpgrade={redirectToUpgrade}
                />
            )}

            {/* Questions modal*/}
            <Modal
                isOpen={questionModalIsOpen}
                onRequestClose={closeQuestionModal}
                className="preview-questions-modal"
            >
                <PreviewQuestion
                    questionId={selectedQuestion}
                    onRequestClose={closeQuestionModal}
                    hasMoreQuestions={false}
                    companyId={effectiveCompanyId}
                    partnerId={partnerId}
                    questionsSelected={[selectedQuestion]}
                    isWizard
                    hideFooter={true}
                    showLanguageSelector={true}
                    defaultLanguage={effectiveDefaultLanguage}
                />
            </Modal>

            {/* Material modal*/}
            <Modal
                isOpen={materialModalIsOpen}
                onRequestClose={closeMaterialModal}
                className="preview-questions-modal"
            >
                <MaterialModal
                    material={selectedMaterial}
                    onRequestClose={closeMaterialModal}
                />
            </Modal>

            <ScormContentModal
                isModalOpen={isAddContentModalOpen}
                onRequestCloseModal={() => {
                    cmi5Store.error = null;
                    setAddContentModalOpen(false);
                }}
                companyId={effectiveCompanyId}
                partnerId={partnerId}
            />
        </Container>
    );
}

export default observer(Content);
